/**
 * `ExportFlow` — orchestrator that drives the informed-export state
 * machine, then composes optional cryptographic erasure invocation
 * per ADR-024 §2.7 at the executing step.
 *
 * Composition contract:
 *
 *   IProjectionServiceClient — both the export-bundle source (via
 *                              the projection record query path) AND
 *                              the erasure invocation surface (via
 *                              `erasePatientProjection` per ADR-024
 *                              §2.7.3 step 5 + scaffold typed
 *                              contract).
 *
 * **Doctrinal posture.** Three load-bearing properties:
 *
 *   1. The informed-export notice (state 'notice-presentation') is
 *      acknowledged by affirmative patient action; the orchestrator
 *      does not advance past the notice without an explicit
 *      `acknowledge-notice` event.
 *   2. The erasure-composition selection (state 'erasure-composition-
 *      selection') has no pre-selected default per ADR-024 §2.7.2;
 *      the orchestrator does not advance until the patient
 *      affirmatively selects one of `none` / `erase-both` /
 *      `erase-holder-only` / `erase-phg-only`.
 *   3. Recovery-failure ≠ erasure per ADR-024 §2.7.5; the export
 *      flow's "leave the substrate" framing is distinct from the
 *      recovery flow's "I lost access" framing — different state
 *      machines, different surfaces.
 *
 * **What the v0 orchestrator does NOT do:**
 *
 *   - Real export-bundle construction (FHIR Bundle / FHIR
 *     AuditEvent Bundle / structured JSON) — depends on having
 *     decrypted content in active session, which gates on item 2
 *     (auth + KEK release). The mock returns a synthetic bundle
 *     reference suitable for confirmation UI.
 *   - Real wrapped-KEK destruction at SHT storage per ADR-024
 *     §2.7.3 step 5 — the orchestrator invokes
 *     `IProjectionServiceClient.erasePatientProjection` which
 *     stubs the cryptographic-erasure operation; backup-coordinated
 *     destruction lands at production wiring.
 *   - Holder-retained-pathway erasure per ADR-024 §2.1-§2.6 — that
 *     is substrate-side machinery (SHT-signed `ErasureRequest`
 *     to substrate parties); the client surfaces the patient's
 *     election and ships it to the SHT-side erasure service via
 *     `holderRetainedErasure` callback supplied by app shell. The
 *     scaffold mock returns a synthetic audit-record reference.
 */
import { applyEvent, initialExportState, isTerminal, } from './export-state.js';
// ---------- Implementation ----------
export class ExportFlow {
    options;
    state = initialExportState();
    observers = new Set();
    constructor(options) {
        this.options = options;
    }
    getState() {
        return this.state;
    }
    subscribe(observer) {
        this.observers.add(observer);
        observer(this.state);
        return () => {
            this.observers.delete(observer);
        };
    }
    beginScopeSelection() {
        if (this.state.kind !== 'intro') {
            return;
        }
        this.transition({ kind: 'begin-scope-selection' });
    }
    selectCategories(categories) {
        if (this.state.kind !== 'scope-selection') {
            return;
        }
        this.transition({ kind: 'select-categories', categories });
    }
    selectFormat(format) {
        if (this.state.kind !== 'format-selection') {
            return;
        }
        this.transition({ kind: 'select-format', format });
    }
    selectDestination(destination) {
        if (this.state.kind !== 'destination-selection') {
            return;
        }
        this.transition({ kind: 'select-destination', destination });
    }
    acknowledgeNotice() {
        if (this.state.kind !== 'notice-presentation') {
            return;
        }
        this.transition({ kind: 'acknowledge-notice' });
    }
    selectErasureComposition(composition) {
        if (this.state.kind !== 'erasure-composition-selection') {
            return;
        }
        this.transition({ kind: 'select-erasure-composition', composition });
    }
    async confirm() {
        if (this.state.kind !== 'confirmation') {
            return;
        }
        const selection = this.state.selection;
        const erasureComposition = this.state.erasureComposition;
        this.transition({ kind: 'confirm' });
        try {
            // Generate the export bundle. v0 scaffold synthesizes a
            // bundle reference; real implementation lands when active
            // session crypto wires (item 2 onward).
            const result = await this.generateExport(selection);
            // Compose erasure if elected.
            let erasureAudit;
            if (erasureComposition !== 'none') {
                erasureAudit = await this.invokeErasure(erasureComposition);
            }
            this.transition({
                kind: 'export-completed',
                result,
                ...(erasureAudit !== undefined ? { erasureAudit } : {}),
            });
        }
        catch (e) {
            this.transition({
                kind: 'error',
                code: 'export-execution-failed',
                message: errorMessage(e),
            });
        }
    }
    cancel(reason) {
        if (isTerminal(this.state)) {
            return;
        }
        this.transition({ kind: 'cancel', reason });
    }
    // ---------- private ----------
    async generateExport(selection) {
        // SCAFFOLD: query the projection-service for matching records
        // (proxy for the real export-bundler that runs in active patient
        // session post-decrypt) and synthesize a bundle reference.
        const page = await this.options.projectionService.queryProjectionRecords({
            ...(this.options.tenantId !== undefined ? { tenantId: this.options.tenantId } : {}),
            request: {
                requestId: `export_${Date.now()}`,
                subjectPciHash: this.options.subjectPciHash,
            },
        });
        const dest = selection.destination.kind === 'downloaded-file'
            ? 'file'
            : selection.destination.appIdentity;
        return {
            bundleRef: `bundle_${selection.format}_${dest}_${Date.now()}`,
            itemCount: page.records.length,
            generatedAt: new Date().toISOString(),
        };
    }
    async invokeErasure(composition) {
        const token = await this.options.mintAuthorizationToken();
        const result = {};
        // Per ADR-024 §2.7.6: outcome 1 (`erase-both`) produces TWO
        // audit-of-erasure records — one for the PHG-resident pathway
        // (this client invokes via projectionService) and one for the
        // holder-retained pathway (substrate-side; routed through the
        // app-shell-supplied callback).
        if (composition === 'erase-phg-only' || composition === 'erase-both') {
            const out = await this.options.projectionService.erasePatientProjection({
                subjectPciHash: this.options.subjectPciHash,
                ...(this.options.tenantId !== undefined ? { tenantId: this.options.tenantId } : {}),
                scope: composition,
                patientAuthorizationToken: token,
            });
            // The mock client returns ok / not-found. Synthesize an audit
            // record id; production wiring returns the real audit-of-
            // erasure record identifier.
            result.phgResidentRecordId = `audit_phg_erasure_${out.outcome}_${Date.now()}`;
        }
        if (composition === 'erase-holder-only' || composition === 'erase-both') {
            const out = await this.options.holderRetainedErasure({
                subjectPciHash: this.options.subjectPciHash,
                patientAuthorizationToken: token,
            });
            result.holderRetainedRecordId = out.auditRecordId;
        }
        return result;
    }
    transition(event) {
        const next = applyEvent(this.state, event);
        if (next === this.state) {
            return;
        }
        this.state = next;
        for (const observer of this.observers) {
            observer(next);
        }
    }
}
function errorMessage(e) {
    if (e instanceof Error) {
        return e.message;
    }
    return String(e);
}
/**
 * Test-+-dev mock holder-retained erasure callback. Production
 * wiring calls into the substrate's PBC-operated erasure service
 * per ADR-024 §2.6.
 */
export function createMockHolderRetainedErasure() {
    let counter = 0;
    return async (_opts) => {
        counter += 1;
        return { auditRecordId: `audit_holder_erasure_mock_${counter}` };
    };
}
//# sourceMappingURL=export-flow.js.map